/**
 * useCopyToClipboard - Copy a value, and say so for a moment
 *
 * The panel's payoff is taking a value away with you, so three different
 * places offer it. They each kept their own `copied` flag and their own
 * two-second timer; this is that, once, with the timer cleared on unmount.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const CONFIRMATION_MS = 2000;

export const useCopyToClipboard = (
  confirmationMs: number = CONFIRMATION_MS
): { copied: boolean; copy: (value: string | null | undefined) => void } => {
  const [copied, setCopied] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    },
    []
  );

  const copy = useCallback(
    (value: string | null | undefined) => {
      if (!value) {
        return;
      }

      navigator.clipboard
        .writeText(value)
        .then(() => {
          setCopied(true);

          if (timeout.current) {
            clearTimeout(timeout.current);
          }
          timeout.current = setTimeout(() => setCopied(false), confirmationMs);
        })
        .catch((error) => {
          console.error('Failed to copy:', error);
        });
    },
    [confirmationMs]
  );

  return { copied, copy };
};
